import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import {
  adminListArticles,
  adminDeleteArticle,
  adminUpdateArticle,
} from "@/lib/api";

const StatusBadge = ({ status }) => {
  const isPub = status === "published";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-mono-display border ${
        isPub
          ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-300"
          : "bg-amber-500/10 border-amber-400/30 text-amber-300"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isPub ? "bg-emerald-400" : "bg-amber-400"}`} />
      {status}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("id-ID", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      const data = await adminListArticles();
      setArticles(data || []);
    } catch (e) {
      toast.error("Gagal memuat artikel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (a) => {
    if (!window.confirm(`Hapus artikel "${a.title}"? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await adminDeleteArticle(a.id);
      toast.success("Artikel dihapus");
      setArticles((cur) => cur.filter((x) => x.id !== a.id));
    } catch {
      toast.error("Gagal menghapus artikel");
    }
  };

  const togglePublish = async (a) => {
    const next = a.status === "published" ? "draft" : "published";
    try {
      const updated = await adminUpdateArticle(a.id, { status: next });
      setArticles((cur) => cur.map((x) => (x.id === a.id ? updated : x)));
      toast.success(next === "published" ? "Artikel dipublikasikan" : "Artikel disimpan sebagai draft");
    } catch {
      toast.error("Gagal mengubah status");
    }
  };

  const filtered = articles.filter((a) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q) ||
      (a.slug || "").toLowerCase().includes(q)
    );
  });

  const publishedCount = articles.filter((a) => a.status === "published").length;
  const draftCount = articles.length - publishedCount;

  return (
    <AdminLayout
      title="Articles"
      action={
        <Link
          to="/admin/articles/new"
          data-testid="new-article-btn"
          className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium shadow-[0_0_24px_rgba(59,130,246,0.35)] hover:shadow-[0_0_40px_rgba(59,130,246,0.55)] transition-shadow"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          New article
        </Link>
      }
    >
      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
        <Stat label="Total" value={articles.length} />
        <Stat label="Published" value={publishedCount} tone="emerald" />
        <Stat label="Draft" value={draftCount} tone="amber" />
        <Stat label="Categories" value={new Set(articles.map((a) => a.category)).size} />
      </div>

      {/* Search */}
      <div className="mb-6 flex items-center gap-3 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, category, slug…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400/40"
          />
        </div>
        <span className="text-xs text-slate-500 font-mono-display whitespace-nowrap">
          {filtered.length} / {articles.length}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-2xl glass-elevated overflow-hidden" data-testid="admin-articles-table">
        <div className="hidden md:grid grid-cols-[1fr_140px_120px_120px_120px] gap-4 px-6 py-3 border-b border-white/[0.06] text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono-display">
          <div>Title</div>
          <div>Category</div>
          <div>Status</div>
          <div>Updated</div>
          <div className="text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="admin-loading">
            Loading articles…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="admin-empty">
            <div className="text-slate-300 text-base mb-1" style={{ fontFamily: "Outfit" }}>
              {articles.length === 0 ? "Belum ada artikel" : "Tidak ada hasil"}
            </div>
            <p className="text-sm text-slate-500">
              {articles.length === 0
                ? "Klik 'New article' untuk membuat artikel pertama Anda."
                : "Coba kata kunci pencarian lain."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.05]">
            {filtered.map((a) => (
              <li
                key={a.id}
                data-testid={`admin-article-row-${a.id}`}
                className="grid grid-cols-1 md:grid-cols-[1fr_140px_120px_120px_120px] gap-3 md:gap-4 px-5 sm:px-6 py-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-white font-medium truncate" style={{ fontFamily: "Outfit" }}>
                    {a.title}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 font-mono-display truncate">
                    /{a.slug}
                  </div>
                </div>
                <div className="text-xs text-slate-300">{a.category}</div>
                <div>
                  <button
                    onClick={() => togglePublish(a)}
                    data-testid={`toggle-status-${a.id}`}
                    title="Toggle publish status"
                  >
                    <StatusBadge status={a.status} />
                  </button>
                </div>
                <div className="text-xs text-slate-400 font-mono-display">{formatDate(a.updated_at)}</div>
                <div className="flex items-center justify-end gap-1.5">
                  {a.status === "published" && (
                    <a
                      href={`/#blog`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg text-slate-400 hover:text-blue-300 hover:bg-white/[0.04] transition-colors"
                      title="View on site"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    data-testid={`edit-article-${a.id}`}
                    onClick={() => navigate(`/admin/articles/edit/${a.id}`)}
                    className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-article-${a.id}`}
                    onClick={() => handleDelete(a)}
                    className="p-2 rounded-lg text-slate-300 hover:text-red-300 hover:bg-red-500/[0.06] transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
};

const Stat = ({ label, value, tone }) => {
  const toneCls = {
    emerald: "from-emerald-500/15 to-emerald-500/0",
    amber: "from-amber-500/15 to-amber-500/0",
  }[tone] || "from-blue-500/15 to-blue-500/0";

  return (
    <div className={`relative rounded-2xl p-5 glass-elevated overflow-hidden`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${toneCls} pointer-events-none`} />
      <div className="relative text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono-display">
        {label}
      </div>
      <div className="relative mt-2 text-3xl text-white font-medium" style={{ fontFamily: "Outfit" }}>
        {value}
      </div>
    </div>
  );
};

export default Admin;
